import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Chip,
  CircularProgress,
  Alert,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Search, UserCheck, UserX, Trash2, RotateCcw, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { adminApiService } from '../../services/adminApiService';

interface CognitoUserAttribute {
  Name: string;
  Value: string;
}

interface CognitoUser {
  Username: string;
  UserStatus: string;
  UserCreateDate: string;
  UserLastModifiedDate: string;
  Enabled: boolean;
  Attributes: CognitoUserAttribute[];
}

interface UserTableRow {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  status: string;
  createdDate: string;
  lastModifiedDate: string;
  enabled: boolean;
}

const UserManagement: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<CognitoUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredUsers, setFilteredUsers] = useState<UserTableRow[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserTableRow | null>(null);
  const [actionDialog, setActionDialog] = useState<{
    open: boolean;
    action: 'enable' | 'disable' | 'delete' | null;
  }>({ open: false, action: null });
  
  // Admin authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  // Check authentication on component mount
  useEffect(() => {
    const checkAuth = async () => {
      const isValid = await adminApiService.validateToken();
      if (isValid) {
        setIsAuthenticated(true);
      } else {
        setShowPasswordModal(true);
      }
    };

    checkAuth();
  }, []);

  // Convert API user to UserTableRow
  const convertUserToTableRow = useCallback((user: CognitoUser): UserTableRow => {
    const getAttributeValue = (attributeName: string): string => {
      if (!user.Attributes) return '';
      const attribute = user.Attributes.find((attr: CognitoUserAttribute) => attr.Name === attributeName);
      return attribute?.Value || '';
    };

    return {
      id: user.Username || '',
      username: user.Username || '',
      email: getAttributeValue('email'),
      firstName: getAttributeValue('given_name'),
      lastName: getAttributeValue('family_name'),
      status: user.UserStatus || '',
      createdDate: user.UserCreateDate || '',
      lastModifiedDate: user.UserLastModifiedDate || '',
      enabled: user.Enabled || false,
    };
  }, []);

  const fetchUsers = useCallback(async () => {
    if (!isAuthenticated) return;
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('Fetching users from admin API...');
      const result = await adminApiService.getUsers();
      
      if (result.success && result.users) {
        console.log('Successfully fetched users:', result.users.length);
        setUsers(result.users);
        
        // Convert to table rows for filtering and display
        const tableRows = result.users.map(convertUserToTableRow);
        setFilteredUsers(tableRows);
      } else {
        setError(result.error || 'Failed to fetch users');
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Network error occurred');
    } finally {
      setLoading(false);
    }
  }, [convertUserToTableRow, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchUsers();
    }
  }, [fetchUsers, isAuthenticated]);

  useEffect(() => {
    if (!searchTerm) {
      const tableRows = users.map(convertUserToTableRow);
      setFilteredUsers(tableRows);
    } else {
      const tableRows = users.map(convertUserToTableRow);
      const filtered = tableRows.filter(user =>
        user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.lastName.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
    }
  }, [searchTerm, users, convertUserToTableRow]);

  const handleAuthenticated = async () => {
    setShowPasswordModal(false);
    setIsAuthenticated(true);
  };

  const handleApiLogin = async (password: string): Promise<boolean> => {
  try {
    
    const apiUrl = import.meta.env.VITE_ADMIN_API_URL || 'http://localhost:3001/api/admin';
    
    const response = await fetch(`${apiUrl}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password })
    });

    const data = await response.json();

    if (response.ok && data.success) {
      localStorage.setItem('admin_api_token', data.token);
      return true;
    } else {
      setError(data.error || 'Login failed');
      return false;
    }
  } catch (error) {
    console.error('Login error:', error);
    setError('Network error - check if server is running');
    return false;
  }
};

  const handleLogout = () => {
    adminApiService.logout();
    setIsAuthenticated(false);
    
    // Also clear session storage for consistency
    sessionStorage.removeItem('admin_authenticated');
    sessionStorage.removeItem('admin_auth_time');
    
    navigate('/');
  };

  const handleUserAction = async (action: 'enable' | 'disable' | 'delete') => {
    if (!selectedUser) return;

    try {
      console.log(`Attempting to ${action} user:`, selectedUser.username);
      
      let result;
      switch (action) {
        case 'enable':
          result = await adminApiService.enableUser(selectedUser.username);
          break;
        case 'disable':
          result = await adminApiService.disableUser(selectedUser.username);
          break;
        case 'delete':
          result = await adminApiService.deleteUser(selectedUser.username);
          break;
      }
      
      if (result.success) {
        console.log(`Successfully ${action}d user:`, selectedUser.username);
        
        // Refresh the user list
        await fetchUsers();
        
        // Close dialog
        setActionDialog({ open: false, action: null });
        setSelectedUser(null);
      } else {
        setError(result.error || `Failed to ${action} user`);
      }
      
    } catch (err) {
      console.error(`Error ${action} user:`, err);
      setError(`Failed to ${action} user`);
    }
  };

  const openActionDialog = (user: UserTableRow, action: 'enable' | 'disable' | 'delete') => {
    setSelectedUser(user);
    setActionDialog({ open: true, action });
  };

  const closeActionDialog = () => {
    setActionDialog({ open: false, action: null });
    setSelectedUser(null);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (status.toLowerCase()) {
      case 'confirmed':
        return 'success';
      case 'unconfirmed':
        return 'warning';
      case 'archived':
        return 'error';
      case 'compromised':
        return 'error';
      case 'unknown':
        return 'default';
      case 'reset_required':
        return 'warning';
      case 'force_change_password':
        return 'warning';
      default:
        return 'default';
    }
  };

  const exportToCSV = () => {
  const headers = ['Username', 'Email', 'First Name', 'Last Name', 'Status', 'Enabled', 'Created Date', 'Last Modified'];
  const csvContent = [
    headers.join(','),
    ...filteredUsers.map(user => [
      `"${user.username}"`,
      `"${user.email}"`,
      `"${user.firstName}"`,
      `"${user.lastName}"`,
      `"${user.status}"`,
      `"${user.enabled ? 'Yes' : 'No'}"`,
      `"${formatDate(user.createdDate)}"`,
      `"${formatDate(user.lastModifiedDate)}"`
    ].join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `users-${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

  // Custom password modal that uses API login
const CustomPasswordModal = ({ open, onAuthenticated }: { open: boolean; onAuthenticated: () => void }) => {
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');

    console.log('🔐 Attempting login with password:', password);

    try {
      const apiUrl = import.meta.env.VITE_ADMIN_API_URL || 'http://localhost:3001/api/admin';
      console.log('🌐 API URL:', apiUrl);

      const response = await fetch(`${apiUrl}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });

      console.log('📡 Response status:', response.status);
      const data = await response.json();
      console.log('📄 Response data:', data);

      if (response.ok && data.success) {
        console.log('✅ Login successful!');
        localStorage.setItem('admin_api_token', data.token);
        onAuthenticated();
        setPassword('');
      } else {
        console.log('❌ Login failed:', data.error);
        setLoginError(data.error || 'Login failed');
      }
    } catch (error) {
      console.error('🚨 Network error:', error);
      setLoginError('Network error - check if server is running');
    } finally {
      setLoginLoading(false);
    }
  };

  return (
    <Dialog open={open} disableEscapeKeyDown>
      <DialogTitle>Admin Authentication</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Admin Password"
            type="password"
            fullWidth
            variant="outlined"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={!!loginError}
            helperText={loginError}
            disabled={loginLoading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            navigate('/');
            // Dispatch event to close admin panel
            window.dispatchEvent(new CustomEvent('admin-cancel'));
          }}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={!password || loginLoading}
          >
            {loginLoading ? <CircularProgress size={20} /> : 'Login'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

  // Show password modal if not authenticated
  if (!isAuthenticated) {
    return (
      <>
        <CustomPasswordModal 
          open={showPasswordModal} 
          onAuthenticated={handleAuthenticated}
        />
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        </Container>
      </>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
    <Box sx={{ mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        User Management
      </Typography>
      <Typography variant="body1" color="text.secondary">
        Securely manage Cognito users via backend API
      </Typography>
    </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

<Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
  <TextField
    placeholder="Search users..."
    value={searchTerm}
    onChange={(e) => setSearchTerm(e.target.value)}
    InputProps={{
      startAdornment: (
        <InputAdornment position="start">
          <Search size={20} />
        </InputAdornment>
      ),
    }}
    sx={{ minWidth: 300 }}
  />
  
  <Button
    variant="outlined"
    startIcon={<RotateCcw size={18} />}
    onClick={fetchUsers}
  >
    Refresh
  </Button>
  
  <Button
    variant="contained"
    startIcon={<Download size={18} />}
    onClick={exportToCSV}
    disabled={filteredUsers.length === 0}
  >
    Export CSV
  </Button>
</Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Showing {filteredUsers.length} of {users.length} users
        </Typography>
      </Box>

      <TableContainer component={Paper} elevation={2}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'grey.50' }}>
              <TableCell><strong>Username</strong></TableCell>
              <TableCell><strong>Email</strong></TableCell>
              <TableCell><strong>Name</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Enabled</strong></TableCell>
              <TableCell><strong>Created</strong></TableCell>
              <TableCell><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    {searchTerm ? 'No users found matching your search.' : 'No users found.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredUsers.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>{user.username}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{`${user.firstName} ${user.lastName}`.trim() || 'N/A'}</TableCell>
                  <TableCell>
                    <Chip 
                      label={user.status} 
                      color={getStatusColor(user.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={user.enabled ? 'Yes' : 'No'} 
                      color={user.enabled ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDate(user.createdDate)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {user.enabled ? (
                        <IconButton
                          size="small"
                          onClick={() => openActionDialog(user, 'disable')}
                          color="warning"
                          title="Disable User"
                        >
                          <UserX size={16} />
                        </IconButton>
                      ) : (
                        <IconButton
                          size="small"
                          onClick={() => openActionDialog(user, 'enable')}
                          color="success"
                          title="Enable User"
                        >
                          <UserCheck size={16} />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => openActionDialog(user, 'delete')}
                        color="error"
                        title="Delete User"
                      >
                        <Trash2 size={16} />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Action Confirmation Dialog */}
      <Dialog open={actionDialog.open} onClose={closeActionDialog}>
        <DialogTitle>
          Confirm {actionDialog.action?.charAt(0).toUpperCase()}{actionDialog.action?.slice(1)} User
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {actionDialog.action} user "{selectedUser?.username}"?
            {actionDialog.action === 'delete' && (
              <Box sx={{ mt: 2 }}>
                <Alert severity="warning">
                  This action cannot be undone. The user will be permanently deleted.
                </Alert>
              </Box>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeActionDialog}>Cancel</Button>
          <Button 
            onClick={() => actionDialog.action && handleUserAction(actionDialog.action)}
            color={actionDialog.action === 'delete' ? 'error' : 'primary'}
            variant="contained"
          >
            {actionDialog.action?.charAt(0).toUpperCase()}{actionDialog.action?.slice(1)}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UserManagement;